import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Badge, BackLink, ButtonLink } from "@/components/play";
import { PlayHeadline } from "@/components/brand/PlayHeadline";
import { ArticleVideo } from "@/components/ArticleVideo";
import { authLink } from "@/components/onboarding/authContinuation";
import { categoryName, formatArticleDate, readingMinutes, type InsightArticle } from "@/lib/insights";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { SitePage } from "@/components/SitePage";
import insightsData from "../data/insights.json";

/** Its date and how long it takes to read. */
function ArticleMeta({ article }: { article: InsightArticle }) {
  return (
    <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-7">
      <span className="inline-flex items-center gap-1.5">
        <Calendar className="h-4 w-4" aria-hidden="true" />
        <time dateTime={article.date}>{formatArticleDate(article.date)}</time>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Clock className="h-4 w-4" aria-hidden="true" />
        {readingMinutes(article.content)} min read
      </span>
    </div>
  );
}

/** One article (/insights/:slug): back to News, its photo, or its film up front, then the article, three more and the next step. */
export function InsightArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = insightsData.articles.find((a) => a.slug === slug);
  useDocumentTitle(`${article?.title ?? "Article not found"} · StreamBridge`);

  if (!article) {
    return (
      <SitePage mainId="article-main" mainClassName="px-6 pt-6">
        <BackLink to="/insights">News</BackLink>
        <div className="mx-auto max-w-3xl py-16 text-center">
          <h1 className="text-2xl font-black text-neutral-10">Article not found</h1>
          <p className="mt-2 text-neutral-7">It may have moved. The other articles are in News.</p>
        </div>
      </SitePage>
    );
  }

  return (
    <SitePage mainId="article-main">
      {article.film ? (
        <header className="px-6 pt-6">
          <BackLink to="/insights">News</BackLink>
          <div className="mx-auto mt-8 max-w-4xl">
            <h1 className="site-page__eyebrow">{article.title}</h1>
            <p className="site-page__title site-page__title--article"><PlayHeadline words={article.film.headline} /></p>
            <ArticleVideo wide className="mt-4" src={article.video} poster={article.poster} captions={article.captions} label={`${article.title}: the film`} />
            <div className="mt-4">
              <ArticleMeta article={article} />
            </div>
            <p className="mt-6 text-lg text-neutral-8">{article.description}</p>
          </div>
        </header>
      ) : (
        <>
        <div className="relative h-[40vh] min-h-[300px] md:h-[50vh]">
          <img src={article.image} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-1 from-25% to-transparent" />

          {/* Over the photo, so the ink tone, which reads on any picture. */}
          <div className="absolute left-6 top-6">
            <BackLink to="/insights" tone="inverse">News</BackLink>
          </div>
        </div>

        <header className="relative -mt-32 px-6">
          <div className="mx-auto max-w-3xl">
            <div className="mb-4 flex flex-wrap gap-2">
              {article.categories.map((category) => (
                <Badge key={category}>{categoryName(category)}</Badge>
              ))}
            </div>

            <h1 className="mb-4 text-3xl font-black text-neutral-10 sm:text-4xl md:text-5xl">{article.title}</h1>

            <ArticleMeta article={article} />

            <p className="mt-6 text-lg text-neutral-8">{article.description}</p>
          </div>
        </header>
        </>
      )}

      <article className="px-6 py-16" aria-label={article.title}>
        {!article.film && (
          <ArticleVideo className="mb-12" src={article.video} poster={article.poster} captions={article.captions} label={`${article.title}: the clip`} />
        )}
        <div className="mx-auto max-w-3xl [&>*:first-child]:mt-0">
          <ReactMarkdown
            components={{
              h2: ({ children }) => <h2 className="mb-4 mt-12 text-2xl font-black text-neutral-10">{children}</h2>,
              h3: ({ children }) => <h3 className="mb-3 mt-10 text-xl font-black text-neutral-10">{children}</h3>,
              p: ({ children }) => <p className="mb-6 text-neutral-8">{children}</p>,
              strong: ({ children }) => <strong className="font-medium text-neutral-10">{children}</strong>,
              a: ({ href, children }) => <a href={href} className="text-neutral-10 underline underline-offset-4 hover:text-neutral-8">{children}</a>,
              ol: ({ children }) => <ol className="my-6 list-inside list-decimal space-y-3 text-neutral-8">{children}</ol>,
              ul: ({ children }) => <ul className="my-6 list-inside list-disc space-y-3 text-neutral-8">{children}</ul>,
              li: ({ children }) => <li className="text-neutral-8">{children}</li>,
              // A picture, or a clip in the text: ![what it shows](/videos/insights/<name>.mp4), with its poster and
              // captions beside it by name, and the title "wide" for a 16:9 one.
              img: ({ src = "", alt = "", title }) =>
                src.endsWith(".mp4") ? (
                  <ArticleVideo src={src} poster={src.replace(/\.mp4$/, "-poster.jpg")} captions={src.replace(/\.mp4$/, ".en.vtt")} label={alt} wide={title === "wide"} />
                ) : (
                  <img src={src} alt={alt} className="mx-auto w-full max-w-xl" loading="lazy" />
                ),
            }}
          >
            {article.content}
          </ReactMarkdown>
        </div>
      </article>

      <section className="border-t border-neutral-4 px-6 py-16" aria-labelledby="insights-more-title">
        <div className="mx-auto max-w-5xl">
          <h2 id="insights-more-title" className="mb-8 text-2xl font-black text-neutral-10">More from the workshop</h2>
          <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" role="list">
            {insightsData.articles
              .filter((a) => a.id !== article.id)
              .slice(0, 3)
              .map((related) => (
                <li key={related.id}>
                  <Link to={`/insights/${related.slug}`} className="group block h-full rounded-xl" aria-labelledby={`related-${related.id}-title`}>
                    <article className="h-full overflow-hidden rounded-xl bg-neutral-2">
                      <div className="relative aspect-video overflow-hidden">
                        <img src={related.image} alt="" className="h-full w-full object-cover" loading="lazy" />
                      </div>
                      <div className="p-4">
                        <h3 id={`related-${related.id}-title`} className="font-medium text-neutral-10 underline-offset-4 group-hover:underline">{related.title}</h3>
                        <p className="mt-2 line-clamp-2 text-sm text-neutral-7">{related.description}</p>
                      </div>
                    </article>
                  </Link>
                </li>
              ))}
          </ul>
        </div>
      </section>

      <section className="px-6 pb-24" aria-labelledby="insights-article-cta-title">
        <div className="mx-auto max-w-3xl rounded-2xl bg-neutral-2 p-10 text-center">
          <h2 id="insights-article-cta-title" className="mb-3 text-2xl font-black text-neutral-10">Bring your event to life.</h2>
          <p className="mb-6 text-neutral-7">Create an event and bring your cameras.</p>
          <ButtonLink to={authLink("/signup", "/app?create=1")}>
            Create event
            <ArrowRight aria-hidden="true" />
          </ButtonLink>
        </div>
      </section>
    </SitePage>
  );
}

export default InsightArticlePage;
